import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { ServiceTypeSelectorOption } from "../../../types/order";
import ConfirmDialog from "../../common/ConfirmDialog";

interface StopUsingDialogProps {
  open: boolean;
  onClose: () => void;
  onFinishRental: () => void;
}

const StopUsingDialog = (props: StopUsingDialogProps) => {
  const { t } = useTranslation();
  const { selectedServiceType, selectedBooking } = useSelector(
    (state: RootState) => state.booking
  );
  /**
   * The BOOKING's own service decides the copy, not the orders-page tab.
   *
   * `selectedServiceType` is set by `ServiceTypeSelector`, i.e. only once the
   * customer has passed through the orders list — and it initialises to
   * `RENTAL` (`bookingSlice`). Enter a storage booking by deep link — the OTP
   * "retrieve my booking" link among them — and the selector never mounts, so
   * this dialog would read `rental` for a locker and show product-rental's text:
   * precisely the wrong wording, silently, on the path most likely to be used
   * by someone who lost their booking. The tab remains the fallback for the
   * (parcel) call sites that render this without a selected booking.
   */
  const service = selectedBooking?.service ?? selectedServiceType;
  const isSup = service === ServiceTypeSelectorOption.SUP;
  /**
   * Storage gets its own wording because the DEFAULT text is product-rental's
   * and says the opposite of what a locker customer is confirming: "du den
   * Gegenstand im richtigen Schrank platziert und ihn in seinem ursprünglichen
   * Zustand belassen hast" — putting a borrowed thing back. A storage customer
   * is taking their OWN things out, so the confirmation is that the compartment
   * is empty (PO, KGSt-Forum 2026-09-14).
   *
   * The default keeps serving `p2p`, `parcel`, `keys` and `rental`, where the
   * place-it-back reading is correct.
   */
  const isStorage = service === ServiceTypeSelectorOption.STORAGE;

  const variant = (suffix: string): string => {
    if (isStorage) return `use_order.stop_using.storage.${suffix}`;
    if (isSup) return `use_order.stop_using.sup.${suffix}`;
    return `use_order.stop_using.${suffix}`;
  };

  const onConfirmFinishRental = () => {
    props.onFinishRental();
    props.onClose();
  };

  return (
    <ConfirmDialog
      open={props.open}
      title={t(variant("title"))}
      content={
        /*
         * Plain text, no `<Trans>` interpolation, because no copy here has a
         * placeholder left to interpolate.
         *
         * The default text used to end "Bitte melde eventuelle Probleme unter
         * (i)" and the `(i)` was a DECORATIVE SPAN — no onClick, no href. The
         * sentence promised an action the dialog could not perform, for
         * `rental`, `p2p`, `parcel` and `keys`. The sentence is gone (it was
         * never offered to storage), and with it the icon component, the
         * `Trans` wrapper and the `Assets.icon.infoIcon` lookup that existed
         * only to render it.
         *
         * Reporting a problem is reachable from the welcome screen and the
         * ticket's NB box, both of which actually open the form.
         */
        t(variant("content"))
      }
      cancelText={t("use_order.stop_using.cancel_button")}
      confirmText={t("use_order.stop_using.confirm_button")}
      onClose={props.onClose}
      onConfirm={onConfirmFinishRental}
    />
  );
};

export default StopUsingDialog;
